// The "put these assets on this slide" ask, in one place.
//
// Same reasoning as lib/geometryAsk.ts, which this mirrors: one intent must have
// one wording. The assets picker raises it today; anything else that grows an
// "add this picture" affordance calls this rather than composing its own
// sentence, because three wordings for one intent is how a weak model learns
// three behaviours.
//
// The picker's two verbs SPLIT on this (2026-08-03): "Add to slide" sends this
// ask straight away, "Add to chat" only drops the refs into what the user is
// writing. It used to prefill both, and a button labelled "Add to slide" that
// leaves you a sentence to press send on is indistinguishable from the button
// next to it (Alex, 2026-08-03). The prefill survives as the FALLBACK — see
// `seedIfHeld` in canvas/CommandBar.tsx — so a busy chat or an unreviewed
// proposal on the slide queues the ask rather than dropping or stacking it.
//
// This does NOT generalise to the Edit tab's "Fix in chat" (lib/geometryAsk.ts),
// which still only prefills. That one is a diagnostic handoff the user may want
// to reword or ignore; ticking files and pressing Add is already the decision.

/** The `@assets/<name>` ref for one asset. The token is deliberately the same
 *  string in three places — chat context, the slide tools' media argument, and
 *  the Vite import specifier — so composing it lives here and in the composer's
 *  attach handler, and nowhere else. */
/** A picked asset, as both surfaces hold one. `kind` decides the noun the ask
 *  uses, because a video is placed differently from a photo. */
export type Asset = { name: string; kind?: 'image' | 'video' };

export function assetRef(name: string): string {
  return `@assets/${name}`;
}

/** Refs for a picked list, space-separated, in the order they were ticked. */
export function assetRefs(assets: Asset[]): string {
  return assets.map((a) => assetRef(a.name)).join(' ');
}

/** Add text to whatever is already in the composer, normalising the join to
 *  exactly one space and leaving one behind for what the user types next.
 *  Shared by the paperclip's attach and the assets picker's "add to message" —
 *  two surfaces dropping refs into the same box, which must space identically. */
export function appendToInput(value: string | null | undefined, text: string): string {
  return (value ? value.replace(/\s*$/, ' ') : '') + text + ' ';
}

/**
 * The ask for adding picked assets to the focused slide.
 *
 * Images and video are named separately because they are placed differently: a
 * video wants a frame and a poster, a photo wants a column or a bleed. A mixed
 * pick says "media", which is vague on purpose — it is the one case where
 * naming a type would be wrong for half the selection.
 *
 * The closing instruction is not decoration. Without it the model tends to read
 * "add this image" as "make this an image slide" and throws the existing copy
 * away, which is never what someone dragging in a photo meant.
 */
export function assetAsk(assets: Asset[]): string {
  const refs = assetRefs(assets);
  if (!refs) return '';
  const one = assets.length === 1;
  const kinds = new Set(assets.map((a) => a.kind));
  const word = kinds.size > 1 ? 'file' : kinds.has('video') ? 'video' : 'image';
  const noun = one ? word : `${word}s`;
  const lead = one ? 'Add this' : 'Add these';
  const it = one ? 'it' : 'them';
  return (
    `${lead} ${noun} to the slide: ${refs}. ` +
    `Place ${it} where the layout works best, and keep the content that is already on the slide.`
  );
}
